import React, { useEffect, useRef } from 'react';
import OpenSeadragon from 'openseadragon';
import Annotorious from '@recogito/annotorious-openseadragon';
import { MULTI_SLIDE_VIEWER_PARAMS_URL } from 'constants/urls';
import RubberbandEllipseTool from 'components/ImageViewer/annotorious/tools/ellipse/rubberbandEllipseTool';
import RubberbandFreehandTool from 'components/ImageViewer/annotorious/tools/freehand/rubberbandFreehandTool';
import {
  getFirstSelector,
  parseXywh,
  renderCustomShapes,
} from 'components/ImageViewer/annotorious/shapeRenderer';
import { getAnnotationStyle } from 'components/ImageViewer/annotorious/formatter';
import {
  DefaultQCScoresNames,
  ML_QC_BLURRY_DATA,
} from 'components/QCViewer/constants';
import {
  ANNOTATION_COMMENT_AUTHOR_FONT,
  ANNOTATION_COMMENT_AUTHOR_OFFSET_Y,
  ANNOTATION_COMMENT_CONTENT_FONT,
  ANNOTATION_COMMENT_LINE_OFFSET_Y,
  ANNOTATION_COMMENT_NEW_COMMENT_OFFSET_Y,
  ANNOTATION_COMMENTS_INITIAL_OFFSET_Y,
  ANNOTATION_OBJECT_LINE_COLOR,
  ANNOTATION_OBJECT_LINE_WIDTH,
  ANNOTATIONS_COLOR_LEGEND_FIELD,
  ANNOTATIONS_PROVIDERS,
  CANCEL_CURRENT_MEASURE_KEY_CODE,
  DEFAULT_ANNOTATION_DRAW_TOOL,
  DEFAULT_OVERLAYS_NUMBER,
  DIMENSIONS_MENU,
  HALO_TYPE_FLAG,
  MAX_COMMENT_ONE_LINE_LENGTH,
  ML_QC_OVERLAY_PADDING_COEFFICIENT,
  RED_RGB,
  SLIDE_VIEWER_PIXELS_PER_METER,
  VISIOPHARM_TYPE_FLAG,
} from 'components/ImageViewer/constants';
import { makeStyles } from 'tss-react/mui';
import { SECONDARY_VIEWER } from 'utilities/colors';
import { getCurrentDateFormatted } from 'utilities/dates_and_times';
import { debounce, range } from 'lodash';
import { createFabricPoint } from 'components/ImageViewer/fabric/objectFactories';
import { fabric } from 'fabric';
import {
  DEFAULT_FABRIC_FONT_COLOR,
  DEFAULT_FONT_SIZE,
  DEFAULT_LINE_COLOR,
  DEFAULT_LINE_WIDTH,
  DEFAULT_SHADOW,
} from 'components/ImageViewer/fabric/constants';
import FileSaver from 'file-saver';
import {
  MAX_SCORE_QC_PASSED,
  MIN_SCORE_QC_FAILED,
} from 'components/Modals/constants';
import { MAX_ZOOM_LEVEL_FOR_BASE_VIEWER } from 'components/ImageViewer/viewerBaseConfig';
import {
  convertToRGBA,
  getViewerContainerPositionFromImagePosition,
} from 'components/ImageViewer/extAnnoUtils';

const iconStyles = (theme) => ({
  icon: {
    svg: {
      width: 30,
      height: 30,
      padding: 2,
      boxSizing: 'border-box',

      [theme.breakpoints.down('lg')]: {
        width: 25,
        height: 25,
      },
    },
  },
  highlightedIcon: {
    backgroundColor: `${SECONDARY_VIEWER} !important`,
  },
  separate: {
    paddingTop: '5rem',

    [theme.breakpoints.down('xl')]: {
      paddingTop: '1.5rem',
    },
  },
  hidden: {
    width: '1px',
    height: '1px',
  },
});

const useStyles = makeStyles()((theme) => iconStyles(theme));

export const ToolbarIcon = ({
  id,
  icon,
  className,
  isFullScreen,
  isSeparate,
  isHighlighted,
  ...params
}) => {
  const ref = useRef();
  const { classes } = useStyles();
  const { onMouseDown, onMouseUp } = params;

  useEffect(() => {
    if (!isFullScreen || !ref.current) {
      return;
    }

    if (onMouseDown) ref.current.addEventListener('mousedown', onMouseDown);
    if (onMouseUp) ref.current.addEventListener('mouseup', onMouseUp);

    // eslint-disable-next-line consistent-return
    return () => {
      if (!ref.current) return;

      if (onMouseDown)
        ref.current.removeEventListener('mousedown', onMouseDown);
      if (onMouseUp) ref.current.removeEventListener('mouseup', onMouseUp);
    };
  }, [onMouseDown, onMouseUp, isFullScreen]);

  return (
    <li className={isSeparate ? classes.separate : ''}>
      <a
        id={id}
        {...params}
        ref={ref}
        className={`rounded-border ${classes.icon} ${
          isHighlighted ? classes.highlightedIcon : ''
        }`}
      >
        {icon || <i className={className} />}
      </a>
    </li>
  );
};

export const openMultiSlideParamsNewWindow = ({ rows }) => {
  const slideUUIDs = rows.map((row) => row.uuid);
  const slideUUIDsString = slideUUIDs.toString();
  const url = MULTI_SLIDE_VIEWER_PARAMS_URL.replace(
    ':slideUUIDs',
    slideUUIDsString,
  );

  window.open(url, '_blank');
};

export const qcScoreOverlaysExist = (slide, scoreToDisplay) => {
  if (!slide?.additional_data) {
    return false;
  }

  const hasSlideQCRegions = DefaultQCScoresNames.some(
    (scoreName) => slide.additional_data[scoreName]?.regions,
  );

  if (!hasSlideQCRegions) {
    return false;
  }

  return !(
    scoreToDisplay !== 'all' && !slide.additional_data[scoreToDisplay]?.regions
  );
};

export const isQCScoreFailed = (qcScore) => qcScore >= MIN_SCORE_QC_FAILED;

export const getTopQCScoreRegions = (
  regions,
  limitTo = DEFAULT_OVERLAYS_NUMBER,
) => {
  const sortedRegions = regions.sort(
    (currRegion, nextRegion) =>
      nextRegion.patch_score_converted - currRegion.patch_score_converted,
  );

  return sortedRegions.slice(0, limitTo);
};

export const filterValidQCScoreRegions = (regions) => {
  const validRegions = regions.filter(
    ({ valid }) => valid === undefined || valid,
  );

  return validRegions;
};

export const getFailedQCRegionsByScoreName = (
  slide,
  scoreName,
  limitTo = DEFAULT_OVERLAYS_NUMBER,
) => {
  const validRegions = filterValidQCScoreRegions(
    slide.additional_data[scoreName].regions,
  );
  const failedRegions = validRegions.filter(({ patch_score_converted }) =>
    isQCScoreFailed(patch_score_converted),
  );
  const topFailedRegions = getTopQCScoreRegions(failedRegions, limitTo);

  return topFailedRegions;
};

export const getAllSlideQCScoreRegions = (
  slide,
  qcScoreNames = DefaultQCScoresNames,
) => {
  const qcScoreRegions = qcScoreNames
    .map((scoreName) => slide.additional_data[scoreName].regions)
    .flat();

  return qcScoreRegions;
};

export const getFailedScoreRegionsByEachScore = (
  slide,
  limitTo = DEFAULT_OVERLAYS_NUMBER,
) => {
  const existingScoreNames = DefaultQCScoresNames.filter(
    (scoreName) => slide.additional_data[scoreName]?.regions,
  );

  const allFailedRegions = existingScoreNames
    .map((scoreName) => {
      const failedRegions = getFailedQCRegionsByScoreName(
        slide,
        scoreName,
        limitTo,
      );

      return failedRegions;
    })
    .flat();

  return allFailedRegions;
};

export const isQCScorePassed = (qcScore) => qcScore <= MAX_SCORE_QC_PASSED;

export const createViewerOverlaysByQCRegions = (regions, slideUUID, viewer) => {
  const overlays = regions.map((region, idx) => {
    const { patch_bbox, patch_score_converted } = region;
    // [x1, y1, x2, y2] is the format for the patch_bbox but
    // we need to make some transformations according to OSD slide rotation
    const boxSideLength = patch_bbox[2] - patch_bbox[0];
    let borderClass;

    if (isQCScoreFailed(patch_score_converted)) {
      borderClass = 'red-qc-ml-highlighted-box';
    } else if (isQCScorePassed(patch_score_converted)) {
      borderClass = 'green-qc-ml-highlighted-box';
    } else {
      borderClass = 'yellow-qc-ml-highlighted-box';
    }

    // need some padding to prevent boxes overlapping
    const pad = boxSideLength * ML_QC_OVERLAY_PADDING_COEFFICIENT;

    const qcScoreAreaOverlay = {
      id: `qc-ml-overlay-${slideUUID}-${idx}-${viewer.id}`,
      // these transformation are based on the OSD slide rotation
      // cause originally slides are scanned horizontally but rotated in OSD by 270 degrees
      px: region.patch_bbox[1] + pad,
      py: viewer.source.height - region.patch_bbox[0] - boxSideLength - pad,
      width: boxSideLength - pad * 2,
      height: boxSideLength - pad * 2,
      className: `osd-qc-ml-highlighted-box ${borderClass}`,
    };

    return qcScoreAreaOverlay;
  });

  return overlays;
};

export const getTopQCRegionsByScoreName = (
  slide,
  scoreName,
  limitTo = DEFAULT_OVERLAYS_NUMBER,
) => {
  const validRegions = filterValidQCScoreRegions(
    slide.additional_data[scoreName].regions,
  );
  const topRegions = getTopQCScoreRegions(validRegions, limitTo);

  return topRegions;
};

export const getQCOverlays = (
  viewer,
  slide,
  scoreToDisplay = ML_QC_BLURRY_DATA,
  limitTo = DEFAULT_OVERLAYS_NUMBER,
) => {
  const overlaysRegions =
    scoreToDisplay === 'all'
      ? getFailedScoreRegionsByEachScore(slide, limitTo)
      : getTopQCRegionsByScoreName(slide, scoreToDisplay, limitTo);

  const viewerQCOverlays = createViewerOverlaysByQCRegions(
    overlaysRegions,
    slide.uuid,
    viewer,
  );

  return viewerQCOverlays;
};

export const initAnnotorious = (args) => {
  const {
    viewer,
    annotationsEnabled,
    annotationDrawTool,
    annotationDrawModeEnabled,
    formatter,
    getDrawStyle,
    handleAnnotationUpdate,
    handleAnnotationCreate,
    handleAnnotationRemove,
    // Called with the selected annotation, or null once nothing is selected.
    handleSelectionChange,
  } = args;

  const anno = Annotorious(viewer, {
    formatter,
    // Drawings are saved with an empty body; comments are optional and edited
    // later, so an annotation must not be discarded just because it has none.
    allowEmpty: true,
    // Delete/Backspace are handled by the app (useViewerModeKeyboardShortcuts)
    // so the selected annotation is deleted exactly once, not by both.
    disableDeleteKey: true,
    widgets: [
      {
        widget: 'COMMENT',
      },
    ],
  });

  // Register custom drawing tools before setting the active one
  anno.addDrawingTool(RubberbandEllipseTool);
  anno.addDrawingTool(RubberbandFreehandTool);

  // Annotorious disables its drawing tracker after every completed shape.
  // Remember the mode the app asked for so it can be re-asserted afterwards,
  // keeping the tool active until the user switches back to the cursor.
  let isDrawingMode = !!annotationDrawModeEnabled;
  const setDrawingEnabled = anno.setDrawingEnabled.bind(anno);

  anno.setDrawingEnabled = (enabled) => {
    isDrawingMode = !!enabled;
    setDrawingEnabled(enabled);
  };

  anno.setVisible(annotationsEnabled);
  // The polygon is no longer offered in the tool palette, so a saved polygon
  // tool is treated as the square. Other unknown tools (e.g. one saved by a
  // newer build) fall back to the default: Annotorious throws on a tool it
  // cannot resolve and takes the whole viewer down with it.
  const normalizedDrawTool =
    annotationDrawTool === 'polygon'
      ? DEFAULT_ANNOTATION_DRAW_TOOL
      : annotationDrawTool;
  const isKnownDrawTool = anno.listDrawingTools().includes(normalizedDrawTool);
  anno.setDrawingTool(
    isKnownDrawTool ? normalizedDrawTool : DEFAULT_ANNOTATION_DRAW_TOOL,
  );
  anno.setDrawingEnabled(annotationDrawModeEnabled);

  // Ellipse and pencil annotations are stored as FragmentSelectors (the only
  // reliably renderable format in this Annotorious build), which it draws as
  // rectangles. Swap those for the real geometry whenever it re-renders.
  const annotationLayer = viewer.element.querySelector('.a9s-annotationlayer');

  // The rubberband preview annotorious draws uses its own default styling, so
  // colour/stroke only appeared after a shape had been saved. Paint the
  // in-progress shape with the active style instead.
  const styleSelectionPreviews = () => {
    const style = getDrawStyle?.();

    if (!annotationLayer || !style?.color) {
      return;
    }

    annotationLayer.querySelectorAll('.a9s-selection').forEach((element) => {
      const tag = element.tagName.toLowerCase();

      element.style.stroke = style.color;
      element.style.strokeWidth = `${Math.max(2, style.strokeWidth || 3)}px`;
      element.style.vectorEffect = 'non-scaling-stroke';

      if (tag === 'path' || tag === 'polyline' || tag === 'line') {
        element.style.fill = 'none';
      } else {
        element.style.fill = style.color;
        element.style.fillOpacity = 0.12;
      }
    });
  };

  const applyCustomShapes = () => {
    renderCustomShapes(annotationLayer, anno.getAnnotations());
    styleSelectionPreviews();
  };
  const customShapeObserver = annotationLayer
    ? new MutationObserver(() =>
      window.requestAnimationFrame(applyCustomShapes),
    )
    : null;

  if (customShapeObserver) {
    customShapeObserver.observe(annotationLayer, {
      childList: true,
      subtree: true,
    });
    window.requestAnimationFrame(applyCustomShapes);
  }

  anno.on('updateAnnotation', (annotation, previous) => {
    handleAnnotationUpdate(annotation, previous);
    applyCustomShapes();
    handleSelectionChange?.(null);
  });
  anno.on('createAnnotation', (annotation, overrideId) => {
    handleAnnotationCreate(annotation, overrideId);
    applyCustomShapes();
    handleSelectionChange?.(null);
    // Annotorious switched its drawing tracker off when the shape was
    // completed (onDrawingComplete). This event fires from inside its commit,
    // after that, so re-enabling here is deterministic: the next drag draws.
    if (isDrawingMode) anno.setDrawingEnabled(true);
  });
  anno.on('deleteAnnotation', (annotation) => {
    handleAnnotationRemove(annotation);
    handleSelectionChange?.(null);
  });
  anno.on('selectAnnotation', (annotation) =>
    handleSelectionChange?.(annotation),
  );
  anno.on('startSelection', () =>
    window.requestAnimationFrame(styleSelectionPreviews),
  );

  // Annotorious 2.7 creates a draft 'selection' while drawing; it only becomes
  // a saved annotation when it is committed. Commit it right away so drawing a
  // shape persists without having to confirm an (empty) comment editor, and
  // keep the drawing tool active for the next shape.
  anno.on('createSelection', () => {
    anno.saveSelected();
    // The createAnnotation handler above re-enables drawing as soon as the
    // shape is committed; this is only a fallback for a commit that does not
    // get there (e.g. a selection Annotorious drops instead of saving).
    window.setTimeout(() => {
      if (isDrawingMode) anno.setDrawingEnabled(true);
    }, 350);
  });

  // Annotorious switches its drawing tracker off while it completes a shape
  // (onDrawingComplete runs inside OSD's release handling) and only turns the
  // selection into an annotation on a later tick. A press that follows the
  // release closely therefore found the tracker off and was lost to OSD
  // instead. Re-assert the mode as soon as OSD has processed the release.
  const reassertDrawingAfterRelease = () => {
    if (isDrawingMode) anno.setDrawingEnabled(true);
  };

  viewer.canvas?.addEventListener('pointerup', reassertDrawingAfterRelease);
  viewer.canvas?.addEventListener('pointercancel', reassertDrawingAfterRelease);

  // It's needed cause there is Annotorious bug causes switching draw mode after drawing canceled
  anno.on('cancelSelected', () => {
    handleSelectionChange?.(null);
    window.setTimeout(() => {
      if (isDrawingMode) anno.setDrawingEnabled(true);
    }, 100);
  });

  const destroyAnno = anno.destroy?.bind(anno);

  if (destroyAnno) {
    anno.destroy = () => {
      customShapeObserver?.disconnect();
      viewer.canvas?.removeEventListener(
        'pointerup',
        reassertDrawingAfterRelease,
      );
      viewer.canvas?.removeEventListener(
        'pointercancel',
        reassertDrawingAfterRelease,
      );
      destroyAnno();
    };
  }

  return anno;
};

export const addOverlaysToViewer = (viewer, overlays) => {
  overlays.forEach((overlay) => viewer.addOverlay(overlay));
};

export const getAnnotationByInnerID = (rawAnnotations, innerID) =>
  rawAnnotations.find((rawAnnotation) => rawAnnotation.data.id === innerID);

export const getAnnotationCommentCreatorByUser = (user) => ({
  id: '#',
  name: user.first_name,
});

export const buildAnnotationCommentByResponseData = (comment) => ({
  ...comment,
  creator: getAnnotationCommentCreatorByUser(comment.user),
});

export const buildAnnotationByResponseData = (annotation) => ({
  ...annotation?.data,
  body: annotation.comments.map(buildAnnotationCommentByResponseData),
});

export const getUpdatedRawAnnotations = (
  rawAnnotations,
  updatedAnnotations,
  annotationToUpdate,
) => {
  const updatedAnnotation = updatedAnnotations.find(
    ({ id }) => id === annotationToUpdate.id,
  );

  if (updatedAnnotation) {
    const rawAnnotationToUpdateIdx = rawAnnotations.findIndex(
      ({ data }) => data.id === annotationToUpdate.id,
    );

    const updatedRawAnnotation = {
      ...rawAnnotations[rawAnnotationToUpdateIdx],
      comments: [...updatedAnnotation.body],
    };

    const updatedRawAnnotations = [...rawAnnotations];
    updatedRawAnnotations.splice(
      rawAnnotationToUpdateIdx,
      1,
      updatedRawAnnotation,
    );

    return updatedRawAnnotations;
  }

  return rawAnnotations.filter(({ data }) => data.id !== annotationToUpdate.id);
};

const getFirstTargetSelector = (annotation) => {
  const target = Array.isArray(annotation?.target)
    ? annotation.target[0]
    : annotation?.target;

  return target?.selector;
};

/**
 * Whether two W3C annotations describe the same thing on the slide: same
 * geometry and same comments. Everything else (target source, creator
 * metadata) can legitimately differ between the Annotorious layer and the
 * server response without the shape having to be re-rendered.
 */
export const haveSameAnnotationContent = (annotation, otherAnnotation) =>
  JSON.stringify(getFirstTargetSelector(annotation)) ===
    JSON.stringify(getFirstTargetSelector(otherAnnotation)) &&
  JSON.stringify(annotation?.body || []) ===
    JSON.stringify(otherAnnotation?.body || []);

export const updateAnnotationComments = (annotation, comment) => ({
  ...annotation,
  comments: [...annotation.comments, comment],
});

export const getCreatedComment = (annotation, prevAnnotation) => {
  const prevAnnotationCommentUUIDs = prevAnnotation.body.map(
    (comment) => comment.uuid,
  );

  const createdComment = annotation.body.find(
    (comment) => !prevAnnotationCommentUUIDs.includes(comment.uuid),
  );

  return createdComment;
};

export const getRemovedComments = (annotation, prevAnnotation) => {
  const newAnnotationCommentUUIDs = annotation.body.map(
    (comment) => comment.uuid,
  );

  const removedComments = prevAnnotation.body.filter(
    (comment) => !newAnnotationCommentUUIDs.includes(comment.uuid),
  );

  return removedComments;
};

export const getEditedComments = (annotation, prevAnnotation) => {
  const prevAnnotationCommentValues = Object.fromEntries(
    prevAnnotation.body.map(({ uuid, value }) => [uuid, value]),
  );

  const editedComments = annotation.body.filter((comment) => {
    const prevCommentValue = prevAnnotationCommentValues[comment.uuid];

    return prevCommentValue && comment.value !== prevCommentValue;
  });

  return editedComments;
};

const stopPointerPropagation = (event) => {
  event.stopPropagation();
};

export const createFullScreenButtonElement = (
  id,
  toggleFullScreen,
  isFullScreen,
  classes,
) => {
  const container = document.createElement('div');
  container.classList.add('osd-zoom-button-group', classes.fullScreenButton);
  const button = document.createElement('div');
  button.classList.add('osd-zoom-button', 'osd-rounded-border');
  button.id = `full-page-${id}`;
  button.title = 'Toggle full page';
  button.classList.add('rounded-border');
  const icon = document.createElement('i');
  icon.classList.add('fa', isFullScreen ? 'fa-compress-alt' : 'fa-expand-alt');
  button.appendChild(icon);
  container.appendChild(button);
  container.addEventListener('pointerdown', stopPointerPropagation);
  container.addEventListener('mousedown', stopPointerPropagation);
  container.onclick = toggleFullScreen;

  return container;
};

export const createButtonGroupElement = () => {
  const buttonGroup = document.createElement('div');
  buttonGroup.classList.add('osd-zoom-button-group-container');

  return buttonGroup;
};

const createZoomButtonElement = (zoomAmount, isSelected) => {
  const zoomButton = document.createElement('div');
  zoomButton.className = 'osd-zoom-button osd-rounded-border';
  if (isSelected) {
    zoomButton.classList.add('osd-zoom-btn-selected');
  }
  zoomButton.innerText = `${zoomAmount}x`;

  return zoomButton;
};

// calculate dimensions based on current width and height using multiply param
export const calculateDimension = (width, height, multiply) => {
  const newWidth = width * multiply;
  const newHeight = height * multiply;

  return {
    width: multiply ? newWidth : width,
    height: multiply ? newHeight : height,
  };
};

export const createDimensionsMenu = (
  screenshotWithResizing,
  classes,
  isOpen,
) => {
  const canvas = document.getElementsByClassName('upper-canvas');
  const menuWrapper = document.createElement('ul');
  menuWrapper.className = classes.dimensionMenuWrapper;
  menuWrapper.style = isOpen ? 'display: block' : 'display: none';

  const originWidth = canvas[0]?.width;
  const originHeight = canvas[0]?.height;

  const menuItems = DIMENSIONS_MENU.map((dimension) => {
    const newWidth = dimension?.width;
    const newHeight = dimension?.height;

    const item = document.createElement('li');
    item.textContent = `${dimension.title} (${newWidth || originWidth} x ${
      newHeight || originHeight
    })`;

    item.setAttribute('key', dimension.title);
    item.className = classes.dimensionMenuItem;
    item.addEventListener('pointerdown', stopPointerPropagation);
    item.addEventListener('mousedown', stopPointerPropagation);
    item.onclick = () =>
      screenshotWithResizing({
        width: newWidth || originWidth,
        height: newHeight || originHeight,
      });

    return item;
  });

  menuWrapper.addEventListener('pointerdown', stopPointerPropagation);
  menuWrapper.addEventListener('mousedown', stopPointerPropagation);
  menuWrapper.append(...menuItems);

  return menuWrapper;
};

export const createScreenshotButtonElement = (
  onClickScreen,
  openDimensionsHandler,
  classes,
  isOpenDimensions,
) => {
  const screenshotWithResizing = (resizeValues) => {
    onClickScreen(null, resizeValues);
  };

  const menu = createDimensionsMenu(
    screenshotWithResizing,
    classes,
    isOpenDimensions,
  );
  const screenButtonWrapper = document.createElement('div');
  screenButtonWrapper.className = classes.screenshotWrapper;
  const screenButton = document.createElement('div');
  const dimensionsButton = document.createElement('div');

  screenButton.className = classes.downloadButton;
  screenButton.addEventListener('pointerdown', stopPointerPropagation);
  screenButton.addEventListener('mousedown', stopPointerPropagation);
  screenButton.onclick = onClickScreen;

  dimensionsButton.className = classes.dimensionArrow;
  dimensionsButton.addEventListener('pointerdown', stopPointerPropagation);
  dimensionsButton.addEventListener('mousedown', stopPointerPropagation);
  dimensionsButton.onclick = () => openDimensionsHandler(!isOpenDimensions);

  const icon = document.createElement('i');
  icon.className = 'fa fa-camera';
  screenButton.appendChild(icon);

  const arrowIcon = document.createElement('i');
  arrowIcon.className = 'fa fa-chevron-down';
  dimensionsButton.appendChild(arrowIcon);

  screenButtonWrapper.appendChild(screenButton);
  screenButtonWrapper.appendChild(dimensionsButton);
  screenButtonWrapper.appendChild(menu);

  return screenButtonWrapper;
};

export const createNavSlideButtonElement = (
  text,
  onClick,
  isFullScreen,
  toggleFullScreen,
) => {
  const navButton = document.createElement('div');
  navButton.className = isFullScreen
    ? 'osd-zoom-button osd-rounded-border'
    : 'osd-button-hidden';
  navButton.innerText = text;
  navButton.addEventListener('pointerdown', stopPointerPropagation);
  navButton.addEventListener('mousedown', stopPointerPropagation);
  navButton.onclick = () => {
    toggleFullScreen();
    if (!!onClick && typeof onClick === 'function') onClick(true);
  };

  return navButton;
};

export const getZoomIndicatorText = (zoomValue, actualZoomRatio) => {
  const zoomLabelValue =
    actualZoomRatio > 0 ? zoomValue / actualZoomRatio : zoomValue;

  return `Zoom: ${zoomLabelValue.toFixed(1)}x`;
};

export const createZoomIndicatorElement = (zoomValue, actualZoomRatio) => {
  const zoomIndicator = document.createElement('div');
  zoomIndicator.className = 'osd-zoom-indicator';
  zoomIndicator.style.background = SECONDARY_VIEWER;
  zoomIndicator.innerText = getZoomIndicatorText(zoomValue, actualZoomRatio);

  return zoomIndicator;
};

export const createFixedZoomButton = (
  viewer,
  zoomAmount,
  actualZoomRatio,
  isSelected,
) =>
  new OpenSeadragon.Button({
    element: createZoomButtonElement(zoomAmount, isSelected),
    tooltip: `Zoom to ${zoomAmount}x`,
    onClick: () => {
      const actualZoomValue =
        actualZoomRatio > 0 ? actualZoomRatio * zoomAmount : zoomAmount;
      const zoomVal = parseFloat(actualZoomValue);
      viewer.viewport.zoomTo(zoomVal, null, false);
    },
  });

export const calculateActualZoomRatio = (actualMaxZoomValue) =>
  actualMaxZoomValue / MAX_ZOOM_LEVEL_FOR_BASE_VIEWER;

export const setMaximumZoomToViewerAndViewport = (
  viewer,
  viewerHelper,
  setActualZoomRatio,
) => {
  viewerHelper.setMaxZoom(1);
  const actualMaxZoomLevel =
    viewerHelper.getMaxZoomLevel() || MAX_ZOOM_LEVEL_FOR_BASE_VIEWER;
  viewer.viewport.maxZoomLevel = actualMaxZoomLevel;

  setActualZoomRatio(calculateActualZoomRatio(actualMaxZoomLevel));
};

export const getImagePositionFromViewportPosition = (viewer, position) => {
  const osdPoint = new OpenSeadragon.Point(position.x, position.y);
  const viewportPoint = viewer.viewport.pointFromPixel(osdPoint);
  const imagePoint = viewer.viewport.viewportToImageCoordinates(viewportPoint);

  return imagePoint;
};

export const getDistanceBetweenTwoSlidePoints = (
  position1,
  position2,
  viewer,
) => {
  const imagePoint1 = getImagePositionFromViewportPosition(viewer, position1);
  const imagePoint2 = getImagePositionFromViewportPosition(viewer, position2);
  const { pixelsPerMeter } = viewer;

  const distancePx = Math.hypot(
    imagePoint2.x - imagePoint1.x,
    imagePoint2.y - imagePoint1.y,
  );

  const distanceInMeters = distancePx / pixelsPerMeter;
  const distanceInMm = distanceInMeters * 1000;
  const distanceInMicrometers = distanceInMm * 1000;

  return {
    m: distanceInMeters,
    mm: distanceInMm,
    micrometers: distanceInMicrometers,
  };
};

export const getDistanceMessage = (distance, precision = 3) => {
  const units = [
    { unit: 'km', factor: 1 / 1000000 },
    { unit: 'm', factor: 1 / 1000 },
    { unit: 'cm', factor: 1 / 10 },
    { unit: 'mm', factor: 1 },
    { unit: 'μm', factor: 1000 },
    { unit: 'nm', factor: 1000000 },
  ];

  const defaultUnit = units[3];

  const valueInMM = distance.mm;

  const { unit, factor } =
    units.find(({ factor: f }) => {
      const valueInCurrentUnit = valueInMM * f;
      // make sure the value is between 1 and 1000

      return valueInCurrentUnit >= 1 && valueInCurrentUnit < 1000;
    }) || defaultUnit;

  const value = valueInMM * factor;

  return `${value.toPrecision(precision)} ${unit}`;
};

const getResizedCanvas = (canvas, newWidth, newHeight) => {
  const tmpCanvas = document.createElement('canvas');
  tmpCanvas.width = newWidth;
  tmpCanvas.height = newHeight;

  const ctx = tmpCanvas.getContext('2d');
  ctx.drawImage(
    canvas,
    0,
    0,
    canvas.width,
    canvas.height,
    0,
    0,
    newWidth,
    newHeight,
  );

  return tmpCanvas;
};

// in some cases when viewer is resized image canvas width differs from container width
// leading to wrong objects location (actually its width) on the resulting image
export const getViewerScaleFactor = (viewer) => {
  const imgCanvas = viewer.drawer.canvas;

  const widthScaleFactor = imgCanvas.width / viewer.container.offsetWidth;
  const heightScaleFactor = imgCanvas.height / viewer.container.offsetHeight;

  return {
    widthScaleFactor,
    heightScaleFactor,
  };
};

export const splitAnnotationCommentIntoLines = (comment) => {
  let lineSliceStartIndex = 0;
  let lineSliceEndIndex = MAX_COMMENT_ONE_LINE_LENGTH;

  const commentLinesCount = Math.ceil(
    comment.length / MAX_COMMENT_ONE_LINE_LENGTH,
  );
  const commentLines = range(commentLinesCount).map(() => {
    const commentLine = comment.slice(lineSliceStartIndex, lineSliceEndIndex);

    lineSliceStartIndex = lineSliceEndIndex;
    lineSliceEndIndex += MAX_COMMENT_ONE_LINE_LENGTH;

    return commentLine;
  });

  return commentLines;
};

export const drawAnnotationCommentLines = (
  ctx,
  commentLines,
  startPosition,
) => {
  const commentPosition = { ...startPosition };

  commentLines.forEach((commentLine) => {
    ctx.font = ANNOTATION_COMMENT_CONTENT_FONT;
    ctx.fillText(commentLine, commentPosition.x, commentPosition.y);
    commentPosition.y += ANNOTATION_COMMENT_LINE_OFFSET_Y;
  });
};

export const drawCanvasAnnotationComments = (ctx, comments, startPosition) => {
  const commentPosition = {
    x: startPosition.x,
    y: startPosition.y + ANNOTATION_COMMENTS_INITIAL_OFFSET_Y,
  };

  comments.forEach((comment) => {
    const commentLines = splitAnnotationCommentIntoLines(comment.value);
    drawAnnotationCommentLines(ctx, commentLines, commentPosition);

    const commentsOffsetY =
      commentLines.length * ANNOTATION_COMMENT_LINE_OFFSET_Y;

    ctx.font = ANNOTATION_COMMENT_AUTHOR_FONT;
    const commentAuthor = `${comment.user.first_name} ${comment.user.last_name}`;

    const authorPositionY =
      commentPosition.y + commentsOffsetY - ANNOTATION_COMMENT_AUTHOR_OFFSET_Y;
    ctx.fillText(commentAuthor, commentPosition.x, authorPositionY);
    commentPosition.y =
      authorPositionY + ANNOTATION_COMMENT_NEW_COMMENT_OFFSET_Y;
  });
};

const ELLIPSE_OUTLINE_POINTS = 64;

/**
 * The outline of a rendered annotation in image pixels, read from its W3C
 * data so every shape the viewer can show (rectangle, circle, pencil stroke,
 * legacy polygon) ends up in a screenshot. Null for a shape it cannot draw.
 */
export const getAnnotationOutline = (annotationEl) => {
  const { annotation } = annotationEl || {};
  const data = annotation?.underlying || annotation;
  const selector = data ? getFirstSelector(data) : null;

  if (selector?.shape === 'freehand' && Array.isArray(selector.points)) {
    return {
      points: selector.points.map(([x, y]) => ({ x, y })),
      closed: false,
    };
  }

  if (selector?.type === 'FragmentSelector') {
    const bbox = parseXywh(selector.value);

    if (!bbox) {
      return null;
    }

    if (selector.shape === 'ellipse') {
      const centerX = bbox.x + bbox.w / 2;
      const centerY = bbox.y + bbox.h / 2;
      const points = Array.from({ length: ELLIPSE_OUTLINE_POINTS }, (_, i) => {
        const angle = (i / ELLIPSE_OUTLINE_POINTS) * 2 * Math.PI;

        return {
          x: centerX + (bbox.w / 2) * Math.cos(angle),
          y: centerY + (bbox.h / 2) * Math.sin(angle),
        };
      });

      return { points, closed: true };
    }

    return {
      points: [
        { x: bbox.x, y: bbox.y },
        { x: bbox.x + bbox.w, y: bbox.y },
        { x: bbox.x + bbox.w, y: bbox.y + bbox.h },
        { x: bbox.x, y: bbox.y + bbox.h },
      ],
      closed: true,
    };
  }

  // Legacy polygons are stored as embedded SVG: read the rendered points.
  const firstChild = annotationEl?.firstChild;
  const polygon = firstChild?.animatedPoints
    ? firstChild
    : annotationEl?.querySelector?.('polygon');

  if (polygon?.animatedPoints) {
    return {
      points: Array.from(polygon.animatedPoints, ({ x, y }) => ({ x, y })),
      closed: true,
    };
  }

  return null;
};

export const getViewerImageWithAnnotationsAsCanvas = (viewerCanvas, viewer) => {
  const { widthScaleFactor, heightScaleFactor } = getViewerScaleFactor(viewer);
  const allAnnotations = [
    ...viewer.container.getElementsByClassName('a9s-annotation'),
  ];
  const ctx = viewerCanvas.getContext('2d');

  ctx.lineJoin = 'round';
  ctx.lineCap = 'round';

  allAnnotations.forEach((annotationEl) => {
    const outline = getAnnotationOutline(annotationEl);

    if (!outline?.points.length) {
      return;
    }

    const canvasPoints = outline.points.map(({ x, y }) => {
      const viewerPoint = viewer.viewport.imageToViewerElementCoordinates(
        new OpenSeadragon.Point(x, y),
      );

      return {
        x: viewerPoint.x * widthScaleFactor,
        y: viewerPoint.y * heightScaleFactor,
      };
    });

    // Draw each shape the way the viewer shows it (its own colour and width;
    // strokes on screen do not scale with zoom, so scale them like the canvas).
    const { color, strokeWidth } = getAnnotationStyle(annotationEl.annotation);

    ctx.lineWidth =
      (strokeWidth || ANNOTATION_OBJECT_LINE_WIDTH) * widthScaleFactor;
    ctx.strokeStyle = color || ANNOTATION_OBJECT_LINE_COLOR;
    ctx.beginPath();

    canvasPoints.forEach((point, i) => {
      if (i === 0) {
        ctx.moveTo(point.x, point.y);
      } else {
        ctx.lineTo(point.x, point.y);
      }
    });

    if (outline.closed) {
      ctx.closePath();
    }

    ctx.stroke();

    const recentComments = [
      ...(annotationEl.annotation?.underlying?.body || []),
    ].slice(-3);

    drawCanvasAnnotationComments(ctx, recentComments, {
      x: Math.min(...canvasPoints.map((point) => point.x)),
      y: Math.max(...canvasPoints.map((point) => point.y)),
    });
  });

  return viewerCanvas;
};

export const handleViewerSlideScreenshot = (
  slide,
  viewer,
  distanceMeasureManager,
  resizeValues,
  extAnnotationsManager,
  aiDrawingManager,
  displayedAnnotations,
) => {
  if (!viewer || !slide) {
    return;
  }

  const currentDateFormatted = getCurrentDateFormatted();
  const serializedSlideName = slide.name.replace('.svs', '').replace(/ /g, '_');
  const fileName = `${currentDateFormatted}_${serializedSlideName}.png`;
  const { scalebarInstance } = viewer;
  const scalebarCanvas = scalebarInstance.getImageWithScalebarAsCanvas();

  const mainCanvas = getViewerImageWithAnnotationsAsCanvas(
    scalebarCanvas,
    viewer,
  );

  if (aiDrawingManager?.currentModels) {
    aiDrawingManager.currentModels.forEach((model) => {
      const canvas = model.canvas.upperCanvasEl;
      const ctx = mainCanvas.getContext('2d');
      ctx.drawImage(canvas, 0, 0);
    });
  }

  if (distanceMeasureManager) {
    const distanceMeasureCanvas = distanceMeasureManager.canvas.getElement();
    const mainCanvasContext = mainCanvas.getContext('2d');
    mainCanvasContext.drawImage(distanceMeasureCanvas, 0, 0);
  }

  if (displayedAnnotations.length) {
    extAnnotationsManager.currentModels.forEach((model) => {
      const canvas = model.canvas.upperCanvasEl;
      const ctx = mainCanvas.getContext('2d');
      ctx.drawImage(canvas, 0, 0);
    });
  }

  const resizedCanvas = resizeValues
    ? getResizedCanvas(mainCanvas, resizeValues.width, resizeValues.height)
    : mainCanvas;

  resizedCanvas.toBlob((blob) => {
    FileSaver.saveAs(blob, fileName);
  });
};

export const micronsPerPixelToPixelsPerMeter = (micronsPerPixel) => {
  if (!micronsPerPixel) {
    return SLIDE_VIEWER_PIXELS_PER_METER;
  }

  const metersPerPixel = micronsPerPixel * 1e-6;

  return 1 / metersPerPixel;
};

export class DistanceMeasure {
  constructor(viewer, overlay, canvas) {
    this.imageMeasurePoints = [];
    this.viewer = viewer;
    this.overlay = overlay;
    this.canvas = canvas;
    this.measuredDistance = undefined;
    this.lastDrawnObjects = [];
    this.startPoint = undefined;
    this.isMeasureFinished = false;
  }

  startDraw(position) {
    this.imageMeasurePoints = [];

    const point = createFabricPoint(position.x, position.y);
    this.canvas.add(point);
    const imagePosition = getImagePositionFromViewportPosition(
      this.viewer,
      position,
    );
    this.imageMeasurePoints.push(imagePosition);

    this.startPoint = point;
  }

  drawMeasureLine(position2) {
    const position1 = getViewerContainerPositionFromImagePosition(
      this.viewer,
      this.imageMeasurePoints[0],
    );

    this.canvas.remove(...this.lastDrawnObjects);

    const distance = this.isMeasureFinished
      ? this.measuredDistance
      : getDistanceBetweenTwoSlidePoints(position1, position2, this.viewer);

    this.measuredDistance = distance;

    const distanceMessage = getDistanceMessage(distance);

    const line = new fabric.Line(
      [position1.x, position1.y, position2.x, position2.y],
      {
        strokeWidth: DEFAULT_LINE_WIDTH,
        stroke: DEFAULT_LINE_COLOR,
        lockMovementX: true,
        lockMovementY: true,
        originX: 'center',
        originY: 'center',
        shadow: DEFAULT_SHADOW,
      },
    );

    const text = new fabric.Text(distanceMessage, {
      fontSize: DEFAULT_FONT_SIZE,
      fontWeight: 'bold',
      fontFamily: 'Helvetica',
      left: (position2.x + position1.x) / 2,
      top: (position2.y + position1.y) / 2,
      fill: DEFAULT_FABRIC_FONT_COLOR,
      textAlign: 'center',
      shadow: DEFAULT_SHADOW,
    });

    this.canvas.add(line, text);

    this.canvas.sendToBack(line);
    this.canvas.bringToFront(text);

    this.lastDrawnObjects = [line, text];
  }

  endDraw(position) {
    // fallback for cases when user clicks on the same point twice
    this.drawMeasureLine(position);

    const point = createFabricPoint(position.x, position.y);

    this.canvas.add(point);

    const imagePosition = getImagePositionFromViewportPosition(
      this.viewer,
      position,
    );
    this.imageMeasurePoints.push(imagePosition);

    this.lastDrawnObjects.push(point);
    this.isMeasureFinished = true;
  }

  resize() {
    if (this.isMeasureFinished) {
      this.clear();

      const imagePoints = [...this.imageMeasurePoints];

      const point1 = getViewerContainerPositionFromImagePosition(
        this.viewer,
        imagePoints[0],
      );
      const point2 = getViewerContainerPositionFromImagePosition(
        this.viewer,
        imagePoints[1],
      );

      this.startDraw(point1);
      this.drawMeasureLine(point2);
      this.endDraw(point2);
    } else {
      this.canvas.remove(this.startPoint);
      const point = getViewerContainerPositionFromImagePosition(
        this.viewer,
        this.imageMeasurePoints[0],
      );
      this.startDraw(point);
    }
  }

  /**
   * Re-projects the saved measurement onto the current viewport without
   * recreating fabric objects. Rebuilding them on every zoom frame made the
   * line lag behind the slide while zooming.
   */
  updateViewportPositions() {
    if (!this.isMeasureFinished || this.imageMeasurePoints.length < 2) {
      return;
    }

    const [first, second] = this.imageMeasurePoints.map((point) =>
      getViewerContainerPositionFromImagePosition(this.viewer, point),
    );

    const [line, text, endPoint] = this.lastDrawnObjects;

    if (line?.type === 'line') {
      line.set({ x1: first.x, y1: first.y, x2: second.x, y2: second.y });
      line.setCoords();
    }

    if (text) {
      text.set({
        left: (first.x + second.x) / 2,
        top: (first.y + second.y) / 2,
      });
      text.setCoords();
    }

    if (this.startPoint) {
      this.startPoint.set({ left: first.x, top: first.y });
      this.startPoint.setCoords();
    }

    if (endPoint) {
      endPoint.set({ left: second.x, top: second.y });
      endPoint.setCoords();
    }

    this.canvas.requestRenderAll();
  }

  clear() {
    this.canvas.remove(...this.lastDrawnObjects, this.startPoint);
  }

  show() {
    const objectsToShow = [this.startPoint, ...this.lastDrawnObjects];

    if (!objectsToShow.length) {
      return;
    }
    this.canvas.add(...objectsToShow);
    this.resize();
  }
}

export class DistanceMeasureManager {
  constructor(viewer, isMeasureEnabled) {
    this.viewer = viewer;
    this.isMeasureEnabled = isMeasureEnabled;
    this.measurments = [];
    this.isMeasuring = false;
    this.currentMeasure = undefined;
    this.overlay = undefined;
    this.canvas = undefined;
    this.drawLineMouseTracker = undefined;
  }

  init() {
    this.overlay = this.viewer.fabricjsOverlay({ scale: 1000 });
    this.canvas = this.overlay.fabricCanvas();
    this.addListeners();
  }

  addListeners() {
    const eventsToPreventZooming = [
      'canvas-click',
      // it seems like for some users canvas-drag fires before canvas-click causing measuring to fail
      // "canvas-drag"
    ];

    eventsToPreventZooming.forEach((eventName) => {
      this.viewer.addHandler(eventName, (e) => {
        if (!this.isMeasureEnabled) {
          return;
        }
        e.preventDefaultAction = true;
      });
    });

    // handling dragging to display current measure line properly
    this.viewer.addHandler('canvas-drag', (e) => {
      if (!(this.isMeasureEnabled && this.isMeasuring)) {
        return;
      }

      this.currentMeasure.drawMeasureLine(e.position);
    });

    // Keep finished measurements glued to the slide while zooming/panning.
    // Cheap re-projection per frame instead of rebuilding canvas objects.
    this.viewer.addHandler('viewport-change', () => {
      this.measurments.forEach((measure) => measure.updateViewportPositions());
    });

    this.viewer.addHandler('canvas-click', (e) => {
      // e.quick tells that click wasn't fired on canvas-drag
      if (!(this.isMeasureEnabled && e.quick)) {
        return;
      }

      if (!this.isMeasuring) {
        this.currentMeasure = new DistanceMeasure(
          this.viewer,
          this.overlay,
          this.canvas,
        );
        this.measurments.push(this.currentMeasure);

        this.currentMeasure.startDraw(e.position);
        this.isMeasuring = true;
      } else {
        this.currentMeasure.endDraw(e.position);
        this.isMeasuring = false;
      }
    });

    this.drawLineMouseTracker = new OpenSeadragon.MouseTracker({
      element: this.viewer.container,
      moveHandler: debounce((e) => {
        if (!this.isMeasuring) {
          return;
        }

        this.currentMeasure.drawMeasureLine(e.position);
      }, 5),
      keyUpHandler: (e) => {
        if (e.keyCode === CANCEL_CURRENT_MEASURE_KEY_CODE && this.isMeasuring) {
          e.preventDefault = true;
          this.cancelMeasuring();
        }
      },
    });
  }

  cancelMeasuring() {
    this.currentMeasure.clear();
    this.isMeasuring = false;
    this.currentMeasure = undefined;
    this.measurments.pop();
  }

  reset() {
    this.measurments.forEach((measurement) => measurement.clear());
    this.measurments = [];
    this.isMeasuring = false;
    this.currentMeasure = undefined;
  }

  resize() {
    this.measurments.forEach((measurement) => measurement.resize());
  }

  hide() {
    if (this.isMeasuring) this.cancelMeasuring();
    this.measurments.forEach((measurement) => measurement.clear());
  }

  show() {
    this.measurments.forEach((measurement) => measurement.show());
  }

  destroy() {
    this.overlay?.destroy();
    this.drawLineMouseTracker?.destroy();
  }
}

export const addDefaultViewerEventListeners = (
  viewer,
  {
    setSlideOpened,
    setZoom,
    actualZoomRatio,
    setActualZoomRatio,
    viewerHelper,
  },
) => {
  const handleZoomEvent = (event) => {
    if (!event.eventSource?.source) return;

    const actualZoom = event.zoom;
    const maxActualZoom =
      actualZoomRatio > 0
        ? actualZoomRatio * MAX_ZOOM_LEVEL_FOR_BASE_VIEWER
        : event.zoom;

    if (actualZoom > maxActualZoom) {
      const zoomVal = parseFloat(maxActualZoom);
      viewer.viewport.zoomTo(zoomVal, null, false);
    }

    setZoom(actualZoom > maxActualZoom ? maxActualZoom : actualZoom);
  };

  const handleResizeEvent = (event) => {
    if (!event.eventSource?.source) return;

    setMaximumZoomToViewerAndViewport(viewer, viewerHelper, setActualZoomRatio);
  };

  const handleOpenEvent = () => {
    const actualZoom = viewer?.viewport?.getZoom() || 0;
    viewerHelper.setMaxZoom(1);
    const maxZoom = viewerHelper.getMaxZoomLevel();

    if (actualZoom > maxZoom) {
      viewer.viewport.zoomTo(maxZoom, null, false);
      setZoom(maxZoom);
    }

    setSlideOpened(true);

    setMaximumZoomToViewerAndViewport(viewer, viewerHelper, setActualZoomRatio);
    if (!window.location.hash) {
      viewer.viewport.fitVertically(true);
    }
  };

  viewer.addHandler('zoom', (e) => setTimeout(() => handleZoomEvent(e), 0));
  viewer.addHandler('resize', (e) => setTimeout(() => handleResizeEvent(e), 0));

  viewer.addHandler('open', handleOpenEvent);
  viewer.addHandler('close', () => setSlideOpened(false));

  viewer.addHandler('canvas-drag', () => {
    document.body.style.cursor = 'grabbing';
  });
  viewer.addHandler('canvas-drag-end', () => {
    document.body.style.cursor = 'default';
  });
};

export const getOpenOSDViewers = () =>
  document.querySelectorAll('.openseadragon');

export const setRowSelectionVisible = (gridApi) => {
  const selectedRowIndex = gridApi.getSelectedNodes()?.[0]?.rowIndex;

  if (!selectedRowIndex) return;

  const allRowsCount = gridApi.getDisplayedRowCount();

  const indexToShow =
    selectedRowIndex === allRowsCount - 1
      ? selectedRowIndex
      : selectedRowIndex + 1;

  queueMicrotask(() => {
    gridApi.ensureIndexVisible(indexToShow);
  });
};

const processJSON = ({
  data,
  colorLegend,
  updateColorLegend,
  extAnnoDrawingRef,
  annotationOpacity,
  uuid,
  type,
}) => {
  data.features.forEach((feature, i) => {
    const { legendItemColor } = convertToRGBA(
      feature.properties?.classification?.colorRGB || RED_RGB,
    );
    const updatedColorLegend = {
      ...colorLegend,
      [uuid]: {
        ...colorLegend[uuid],
        [legendItemColor]: {
          label: feature.properties?.classification?.name || i + 1,
          isSelected: true,
        },
      },
    };

    if (!colorLegend[uuid]?.[legendItemColor]) {
      updateColorLegend({
        [ANNOTATIONS_COLOR_LEGEND_FIELD]: updatedColorLegend,
      });
    }

    const modelName = `${feature.properties?.classification?.name} ${i}`;

    const model = extAnnoDrawingRef.current.getOrCreateModel(
      modelName,
      feature,
    );
    if (!colorLegend[uuid]?.[legendItemColor]?.isSelected) {
      model.hide();
    } else {
      model.drawFeature(feature, annotationOpacity, type);
      model.isShown = true;
      model.uuid = uuid;
    }
  });
};

const processXML = ({
  data,
  colorLegend,
  updateColorLegend,
  extAnnoDrawingRef,
  setAnnotationsLegend,
  annotationOpacity,
  uuid,
}) => {
  const parser = new DOMParser();
  const xmlDoc = parser.parseFromString(data, 'text/xml');
  const annotations = xmlDoc.getElementsByTagName('Annotation');

  const annotationsLegend = Array.from(annotations).map((annotation) => {
    const { legendItemColor } = convertToRGBA(
      parseInt(annotation.getAttribute('LineColor'), 10),
    );

    return {
      name: annotation.getAttribute('Name'),
      id: annotation.getAttribute('Id'),
      color: legendItemColor,
      regions: Array.from(annotation.getElementsByTagName('Region')).map(
        (region) => ({
          id: region.getAttribute('Id'),
          area: region.getAttribute('Area'),
          length: region.getAttribute('Length'),
          text: region.getAttribute('Text'),
        }),
      ),
    };
  });
  setAnnotationsLegend({
    uuid,
    annotations: annotationsLegend,
  });

  Array.from(annotations).forEach((annotation, i) => {
    const name = annotation.getAttribute('Name');
    const colorRGB = parseInt(annotation.getAttribute('LineColor'), 10);
    const { legendItemColor } = convertToRGBA(colorRGB);

    const modelName = name + i;

    const updatedColorLegend = {
      ...colorLegend,
      [uuid]: {
        ...colorLegend[uuid],
        [legendItemColor]: {
          label: modelName,
          isSelected: true,
        },
      },
    };
    if (!colorLegend[uuid]?.[legendItemColor]) {
      updateColorLegend({
        [ANNOTATIONS_COLOR_LEGEND_FIELD]: updatedColorLegend,
      });
    }
    const model = extAnnoDrawingRef.current.getOrCreateModel(
      modelName,
      annotation,
    );

    if (!colorLegend[uuid]?.[legendItemColor]?.isSelected) {
      model.hide();
    } else {
      model.drawFeatureFromXML(annotation, annotationOpacity);
      model.isShown = true;
      model.uuid = uuid;
    }
  });
};

const processXMLForVisiopharm = ({
  data,
  extAnnoDrawingRef,
  annotationOpacity,
  uuid,
  mpp,
  colorLegend,
  updateColorLegend,
  type,
}) => {
  const parser = new DOMParser();
  const xmlDoc = parser.parseFromString(data, 'text/xml');
  const annotations = xmlDoc.getElementsByTagName('Object');
  const { legendItemColor } = convertToRGBA(RED_RGB);

  const updatedColorLegend = {
    ...colorLegend,
    [uuid]: {
      ...colorLegend[uuid],
      [legendItemColor]: {
        label: 'Layer 1',
        isSelected: true,
      },
    },
  };

  if (!colorLegend[uuid]?.[legendItemColor]) {
    updateColorLegend({
      [ANNOTATIONS_COLOR_LEGEND_FIELD]: updatedColorLegend,
    });
  }

  Array.from(annotations).forEach((annotation, i) => {
    const layerType = annotation.getAttribute('Type') + i;
    const model = extAnnoDrawingRef.current.getOrCreateModel(
      layerType,
      annotation,
    );
    if (!colorLegend[uuid]?.[legendItemColor]?.isSelected) {
      model.hide();
    } else {
      model.drawFeatureFromVisiopharm(annotation, annotationOpacity, mpp, type);
      model.isShown = true;
      model.uuid = uuid;
    }
  });
};

export const handleInitExternalAnnotations = ({
  extAnnoDrawingRef,
  data,
  uuid,
  setAnnotationsLegend,
  annotationOpacity,
  updateColorLegend,
  colorLegend,
  mpp,
}) => {
  if (!extAnnoDrawingRef.current || !data) {
    return;
  }

  extAnnoDrawingRef.current.currentModels.forEach((model) => {
    if (model.uuid === uuid) model.hide();
  });

  if (data.type === HALO_TYPE_FLAG) {
    processJSON({
      data,
      colorLegend,
      updateColorLegend,
      extAnnoDrawingRef,
      annotationOpacity,
      uuid,
      type: ANNOTATIONS_PROVIDERS.HALO,
    });
  } else if (typeof data === 'string' && data.includes(VISIOPHARM_TYPE_FLAG)) {
    processXMLForVisiopharm({
      data,
      extAnnoDrawingRef,
      annotationOpacity,
      uuid,
      mpp,
      colorLegend,
      updateColorLegend,
      type: ANNOTATIONS_PROVIDERS.VISIOPHARM,
    });
  } else {
    processXML({
      data,
      colorLegend,
      updateColorLegend,
      extAnnoDrawingRef,
      setAnnotationsLegend,
      annotationOpacity,
      uuid,
    });
  }
};
